import React, { useEffect, useState } from 'react';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import Checkbox from '@mui/material/Checkbox';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useTranslation } from 'react-i18next';
import {
  GetTemplate,
  GetTemplateCompanyMaster,
} from '../../../services/apiTypes';
import { Box, FormControlLabel, Typography } from '@mui/material';
import { isRejected } from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../stores/store';
import { updateTemplateMaster } from '../../../stores/template/slice';
import useErrorModal from '../../../hooks/useErrorModal';
import { API_CODE } from '../../../_constants/apiConst';
import { formatDate } from '../../../_utils/utils';
import { TEXT_OVERFLOW_1LINE } from '../../../_constants/styleConst';

type TemplateCompanyModalProps = {
  isOpen: boolean;
  targetTemplate: GetTemplate;
  companyList: GetTemplateCompanyMaster[];
  handleClose: (isSave: boolean) => void;
};

/** 階層に応じて右にずらす */
const StyledTreeItem = styled(TreeItem)(() => ({
  '& .MuiTreeItem-content': {
    display: 'flex',
    alignItems: 'center',
    marginLeft: `calc(var(--TreeView-itemDepth) * 25px)`,
    // ダイアログのwidth(md=900px) - 左右のpadding(24px*2) - marginLeft
    maxWidth: `calc(900px - (24px * 2) - (var(--TreeView-itemDepth) * 25px))`,
  },
}));

/** テンプレートの組織ダイアログ */
const TemplateCompanyModal: React.FC<TemplateCompanyModalProps> = ({
  isOpen,
  targetTemplate,
  companyList,
  handleClose,
}) => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const { getApiErrorMessage } = useErrorModal();
  /** エラーメッセージ */
  const [errorMsg, setErrorMsg] = useState<string>('');
  /** 選択中のカンパニー */
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  /** 展開されているカンパニー */
  const [expandedCompanies, setExpandedCompanies] = useState<string[]>([]);
  /** 保存ボタンが非活性か */
  const [isDisabledSaveBtn, setIsDisabledSaveBtn] = useState<boolean>(false);

  useEffect(() => {
    // 選択状態の初期化
    if (isOpen) {
      const defaultCompanies = targetTemplate.selectedCompanyList.map(
        ({ companyKey }) => companyKey,
      );
      setSelectedCompanies(defaultCompanies);
    }
  }, [isOpen]);

  /** 展開状態の変更 */
  const handleExpandedChange = (
    _: React.SyntheticEvent | null,
    itemIds: string[],
  ) => {
    setExpandedCompanies(itemIds);
  };

  /** ツリーの最上位組織の全て展開ボタン */
  const onClickExpand = (rootKey: string) => {
    // 上位組織Keyのみ抽出
    const primaryAgencyList = companyList.map(
      ({ primaryAgency }) => primaryAgency,
    );

    setExpandedCompanies((prev) => {
      const newExpandSet = new Set([...prev, rootKey]);

      /** 配下のcompanyKeyを再帰的に収集 */
      const collectSubordinateKeys = (parentKey: string) => {
        // 下位組織を抽出
        const children = companyList.filter(
          (c) => c.primaryAgency === parentKey,
        );

        children.forEach(({ companyKey }) => {
          // 下位組織が存在する&未登録
          if (
            primaryAgencyList.includes(companyKey) &&
            !newExpandSet.has(companyKey)
          ) {
            newExpandSet.add(companyKey);
            // 再帰的に子孫を検索
            collectSubordinateKeys(companyKey);
          }
        });
      };

      // 再帰処理を開始
      collectSubordinateKeys(rootKey);

      return Array.from(newExpandSet);
    });
  };

  /** ツリーの最上位組織の全て閉じるボタン */
  const onClickClose = (rootKey: string) => {
    // 上位組織Keyのみ抽出
    const primaryAgencyList = companyList.map(
      ({ primaryAgency }) => primaryAgency,
    );

    setExpandedCompanies((prev) => {
      // 既存の展開済みアイテムで初期化
      const newExpandSet = new Set([...prev, rootKey]);

      /** 配下のcompanyKeyを再帰的に収集 */
      const collectSubordinateKeys = (parentKey: string) => {
        // 直下の組織を抽出
        const children = companyList.filter(
          (c) => c.primaryAgency === parentKey,
        );

        children.forEach(({ companyKey }) => {
          // 下位組織が存在する&未登録
          if (
            primaryAgencyList.includes(companyKey) &&
            !newExpandSet.has(companyKey)
          ) {
            newExpandSet.add(companyKey);
            // 再帰的に子孫を検索
            collectSubordinateKeys(companyKey);
          }
        });
      };

      // 再帰処理を開始
      collectSubordinateKeys(rootKey);

      return prev.filter((item) => !Array.from(newExpandSet).includes(item));
    });
  };

  /** チェックボックスの変更 */
  const onChangeCheck = (isChecked: boolean, companyKey: string) => {
    setSelectedCompanies((prev) => {
      return isChecked
        ? [...prev, companyKey]
        : prev.filter((key) => key !== companyKey);
    });
  };

  /** キャンセルボタン押下時 */
  const onClickCancel = () => {
    setErrorMsg('');
    setExpandedCompanies([]);
    handleClose(false);
  };

  /** 保存ボタン押下時 */
  const onClickSave = async () => {
    setErrorMsg('');
    // 連打防止でボタンを一時的に非活性にする
    setIsDisabledSaveBtn(true);
    const response = await dispatch(
      updateTemplateMaster({
        ...targetTemplate,
        effectiveDate: formatDate(targetTemplate.effectiveDate, 0),
        selectedCompanyList: selectedCompanies,
      }),
    );
    setIsDisabledSaveBtn(false);
    if (isRejected(response)) {
      setErrorMsg(
        getApiErrorMessage(response.payload, API_CODE.TEMPLATE_MASTER),
      );
      return;
    }
    setExpandedCompanies([]);
    handleClose(true);
  };

  const renderTree = (parentKey: string | null) => {
    // ツリーの最上位になる組織のprimaryAgencyを'root'に変換
    const normalizedNodes = companyList.map((company) => {
      // マスタ上の最上位組織
      const isRootCompany = company.companyKey === company.primaryAgency;
      // 取得した一覧に上位組織が無い組織
      const isNoParentCompany = companyList.every(
        ({ companyKey }) => companyKey !== company.primaryAgency,
      );

      return isRootCompany || isNoParentCompany
        ? { ...company, primaryAgency: 'root' }
        : company;
    });

    return normalizedNodes
      .filter((company) => company.primaryAgency === parentKey)
      .map((company, i) => (
        <StyledTreeItem
          key={i}
          itemId={company.companyKey}
          label={
            <Box display="flex" justifyContent="space-between">
              <FormControlLabel
                sx={{
                  ml: 0,
                  '& .MuiFormControlLabel-label': {
                    ...TEXT_OVERFLOW_1LINE,
                    color: 'black!important',
                  },
                }}
                label={company.companyName}
                control={
                  <Checkbox
                    sx={{ mr: '10px', p: 0 }}
                    disabled={targetTemplate.isReadOnly}
                    checked={selectedCompanies.includes(company.companyKey)}
                    onChange={(_, checked) =>
                      onChangeCheck(checked, company.companyKey)
                    }
                  />
                }
              />
              <Box width="190px" display="flex" justifyContent="end">
                {/* 全て展開/閉じるボタン */}
                {company.primaryAgency === 'root' && // ツリーの最上位
                  companyList.some(
                    ({ primaryAgency }) => primaryAgency === company.companyKey, // 下位組織が存在する
                  ) && (
                    <React.Fragment>
                      <Button
                        sx={{ py: 0 }}
                        onClick={() => onClickExpand(company.companyKey)}
                      >
                        {t('LBL_ALL_EXPAND')}
                      </Button>
                      <Button
                        sx={{ py: 0 }}
                        onClick={() => onClickClose(company.companyKey)}
                      >
                        {t('LBL_ALL_COLLAPSE')}
                      </Button>
                    </React.Fragment>
                  )}
              </Box>
            </Box>
          }
        >
          {renderTree(company.companyKey)}
        </StyledTreeItem>
      ));
  };

  return (
    <Dialog open={isOpen} closeAfterTransition={false} fullWidth maxWidth="md">
      <Box display="flex">
        <DialogTitle>{t('LBL_COMPANY_SETTING')}</DialogTitle>
        <Typography alignSelf="center" color="error">
          {errorMsg}
        </Typography>
      </Box>
      <DialogContent dividers style={{ height: '400px', overflowY: 'auto' }}>
        <SimpleTreeView
          expansionTrigger="iconContainer"
          expandedItems={expandedCompanies}
          onExpandedItemsChange={handleExpandedChange}
        >
          {renderTree('root')}
        </SimpleTreeView>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClickCancel}
          sx={{ mx: 2, my: 1, width: '120px' }}
          variant="contained"
          color="inherit"
        >
          {t('BTN_CANCEL')}
        </Button>
        {!targetTemplate.isReadOnly && (
          <Button
            color="primary"
            variant="contained"
            sx={{ mx: 2, my: 1, width: '120px' }}
            disabled={isDisabledSaveBtn}
            onClick={onClickSave}
          >
            {t('BTN_SAVE')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TemplateCompanyModal;
